// ⬡B:aba_shared.ccwa_core.lib.coder_check_in:CODE:banana_pepper_temp_coder_checkin_doctrine:20260718⬡
// ⬡B:aba_shared.ccwa_core.lib.coder_check_in:FIX:normalizer_binds_to_the_real_ccwa_ledger_contract:20260729⬡
// Banana Pepper doctrine repair — the CCWA temporary-coder check-in.
//
// The doctrine: whoever is holding the pen inside the coding department at any
// moment is a TEMPORARY coder. The temporary coder is not the founder and is
// not a fixed identity — it is whichever instance/agent has picked up the work
// for this cycle. Before it touches anything it must "check in": declare its
// active identity (its coder name + LINEAGE, e.g. FOUNDER>CLAUDIA, not a first
// name and not the founder's name), its STATUS, its INTENT / current work, and
// the CYCLE STAMP that anchors the check-in in time. Reporting up is FILTERED:
// routine in-lane work is shown plainly, only genuinely-serious items are
// escalated, and founder-owned or private fields are suppressed rather than
// echoed back.
//
// THE CONTRACT (measured, not guessed): the brain does NOT store one node at a
// fixed source key. It stores MANY rows, one per check-in, under the prefix
// `ccwa.cc.checkin.<coder-slug>.<ts>` with stamp_type CCWA_CHECKIN, and the
// front door that reads them is GET /ccwa/ledger?ham=UID (anew
// routes/ccwa.checkin.routes.js, core/ccwa.first.js). That door returns
//   { ok, ham, count, board: [ { coder, type, source, status, act,
//                                touching, receipts, lineage, lineage_id, at } ] }
// newest first, interleaved CCWA_CHECKIN / CCWA_CHECKOUT / CCWA_MESSAGE /
// CLAIR_LOG. This normalizer takes that board array and derives the CURRENT
// temporary coder: the most recent CCWA_CHECKIN not yet closed by a later
// CCWA_CHECKOUT from the same lineage. Nothing is fabricated. Missing fields
// resolve to explicit "unknown"/null sentinels, never to a guessed default.
//
// 90/10: this file is PURE. It invents no backend, hardcodes no founder, holds
// no React, and reads no clock. It only reshapes the ledger the existing brain
// door already returns into one canonical shape the render layer and the hook
// can both trust.

/**
 * Canonical status vocabulary for a temporary coder. Anything the brain sends
 * that is not in this set collapses to 'unknown' — we never invent a status.
 */
export const CODER_STATUS = Object.freeze({
  IDLE: 'idle',
  CHECKED_IN: 'checked-in',
  WORKING: 'working',
  BLOCKED: 'blocked',
  HANDED_OFF: 'handed-off',
  UNKNOWN: 'unknown',
});

const KNOWN_STATUSES = new Set(Object.values(CODER_STATUS));

// The brain's own row `status` field vocabulary (content.status in the bead):
// START (a live check-in), DONE (a checkout closing one), MESSAGE, ACTIVE (a
// directive), ACK. We map only the two that describe a coder's working state;
// everything else is decided from row TYPE, not this field.
const BRAIN_STATUS = Object.freeze({
  START: CODER_STATUS.WORKING,
  DONE: CODER_STATUS.HANDED_OFF,
});

/**
 * Reporting-visibility bands. Filtered reporting semantics (Banana Pepper
 * doctrine): routine work reports plainly, serious work escalates, and
 * founder/private fields are suppressed from any report echoed back.
 */
export const REPORT_BAND = Object.freeze({
  ROUTINE: 'routine',
  ESCALATE: 'escalate',
  SUPPRESSED: 'suppressed',
});

// Fields that are founder-owned or private and must never be echoed in a
// report. Suppression is by key, so no founder data has to be named here.
const SUPPRESSED_KEYS = new Set([
  'founder',
  'foundername',
  'founder_name',
  'owner',
  'ownername',
  'secret',
  'secrets',
  'apikey',
  'api_key',
  'token',
  'privatenote',
  'private_note',
]);

function str(v) {
  return typeof v === 'string' ? v.trim() : '';
}

function toArray(v) {
  if (Array.isArray(v)) return v;
  return [];
}

/**
 * Normalize a raw status string against the canonical vocabulary.
 * Unknown / empty input resolves to CODER_STATUS.UNKNOWN, never a guess.
 * @param {string} raw
 * @returns {string}
 */
export function normalizeStatus(raw) {
  const upper = str(raw).toUpperCase();
  if (BRAIN_STATUS[upper]) return BRAIN_STATUS[upper];
  const s = str(raw).toLowerCase().replace(/[_\s]+/g, '-');
  return KNOWN_STATUSES.has(s) ? s : CODER_STATUS.UNKNOWN;
}

/**
 * Normalize the active identity of the temporary coder.
 *
 * Doctrine: identity is the coder NAME carried with its LINEAGE (e.g.
 * FOUNDER>CLAUDIA), never a bare first name and never the founder's own name.
 * The brain row carries `coder` (agent_global) and `lineage_id` (the verbatim
 * lineage string the caller sent). We derive a stable display from those two;
 * we do not fabricate either when absent.
 *
 * @param {object} raw - a ledger row, or a { coder, lineageId } block
 * @returns {{ coder: string|null, lineageId: string|null, label: string, complete: boolean }}
 */
export function normalizeIdentity(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const coder = str(src.coder) || str(src.agent_global) || null;
  const lineageId = str(src.lineageId) || str(src.lineage_id) || null;
  const complete = Boolean(coder && lineageId);
  let label;
  if (coder && lineageId && lineageId !== coder) {
    label = `${coder} (${lineageId})`;
  } else if (coder) {
    label = coder;
  } else if (lineageId) {
    label = lineageId;
  } else {
    label = 'unknown coder';
  }
  return { coder, lineageId, label, complete };
}

/**
 * Normalize the lineage chain from a lineage_id string. The brain stores
 * lineage as a `>`-joined chain (FOUNDER>CLAUDIA>VEGA), oldest → current.
 * Returns [] when there is no lineage, never null.
 *
 * @param {string|Array} raw - lineage_id string (or a pre-split array)
 * @returns {Array<{ label: string }>}
 */
export function normalizeLineage(raw) {
  let parts;
  if (Array.isArray(raw)) {
    parts = raw.map((x) => (typeof x === 'object' ? str(x.label) || str(x.lineage_id) : str(x)));
  } else {
    parts = str(raw).split('>');
  }
  return parts.map((p) => str(p)).filter(Boolean).map((label) => ({ label }));
}

/**
 * Decide the reporting band for one work item under filtered reporting
 * semantics.
 *  - explicit serious/incident/security/prod flags  → ESCALATE
 *  - a suppressed/private key present as the item id → SUPPRESSED
 *  - everything else                                 → ROUTINE
 *
 * @param {*} item - either a string (a `touching` entry) or an object
 * @returns {string} one of REPORT_BAND
 */
export function classifyReport(item) {
  if (typeof item === 'string') {
    return SUPPRESSED_KEYS.has(item.trim().toLowerCase()) ? REPORT_BAND.SUPPRESSED : REPORT_BAND.ROUTINE;
  }
  const it = item && typeof item === 'object' ? item : {};
  if (SUPPRESSED_KEYS.has(str(it.key).toLowerCase())) return REPORT_BAND.SUPPRESSED;
  const serious =
    it.serious === true ||
    it.incident === true ||
    it.security === true ||
    it.prod === true ||
    str(it.severity).toLowerCase() === 'high' ||
    str(it.severity).toLowerCase() === 'critical';
  return serious ? REPORT_BAND.ESCALATE : REPORT_BAND.ROUTINE;
}

/**
 * Apply filtered reporting semantics to a work list (the `touching` /
 * `receipts` arrays a check-in carries). Suppressed items are dropped entirely
 * (not echoed). Every surviving item is tagged with its band so the render
 * layer can style routine vs escalated without re-deriving the rule.
 *
 * @param {Array} work
 * @returns {{ items: Array, escalations: number, suppressed: number }}
 */
export function filterReport(work) {
  const list = toArray(work);
  let escalations = 0;
  let suppressed = 0;
  const items = [];
  for (const raw of list) {
    const band = classifyReport(raw);
    if (band === REPORT_BAND.SUPPRESSED) {
      suppressed += 1;
      continue;
    }
    if (band === REPORT_BAND.ESCALATE) escalations += 1;
    const isStr = typeof raw === 'string';
    items.push({
      key: isStr ? str(raw) : str(raw?.key) || null,
      summary: isStr ? str(raw) : str(raw?.summary) || str(raw?.title) || '',
      band,
    });
  }
  return { items, escalations, suppressed };
}

/**
 * Normalize a cycle stamp. The stamp anchors the check-in in time; we keep the
 * raw stamp string exactly as the brain wrote it (the acl_stamp / `lineage`
 * field, e.g. ⬡B:ccwa.command_center:CCWA_CHECKIN:...:20260729⬡) and, when it
 * carries a parseable YYYYMMDD, expose that as `date`. No stamp is ever
 * manufactured client-side.
 *
 * @param {string} raw
 * @returns {{ stamp: string|null, date: string|null }}
 */
export function normalizeCycleStamp(raw) {
  const stamp = str(raw) || null;
  let date = null;
  if (stamp) {
    const m = stamp.match(/(\d{8})/);
    if (m) date = m[1];
  }
  return { stamp, date };
}

// A row is a live CCWA_CHECKIN.
function isCheckin(row) {
  return str(row?.type).toUpperCase() === 'CCWA_CHECKIN';
}
// A row is a CCWA_CHECKOUT (closes a check-in for its lineage).
function isCheckout(row) {
  return str(row?.type).toUpperCase() === 'CCWA_CHECKOUT';
}

/**
 * Normalize the CCWA ledger (GET /ccwa/ledger `board` array) into the one
 * canonical shape the CCWA render layer consumes, deriving the CURRENT
 * temporary coder.
 *
 * Rules, all derived from the ledger, nothing invented:
 *  - The current coder is the most recent CCWA_CHECKIN whose lineage has NOT
 *    since been closed by a later CCWA_CHECKOUT.
 *  - If every check-in has been checked out (or there are none), the pen is
 *    idle: present=false.
 *  - Accepts either the raw door response `{ board: [...] }` or a bare array,
 *    so the hook can hand it whichever it has.
 *
 * @param {object|Array} raw - GET /ccwa/ledger response, or its board array
 * @returns {{
 *   identity: object,
 *   lineage: Array,
 *   status: string,
 *   currentWork: string|null,
 *   report: object,
 *   cycle: object,
 *   present: boolean
 * }}
 */
export function normalizeCheckIn(raw) {
  const board = Array.isArray(raw)
    ? raw
    : toArray(raw && typeof raw === 'object' ? raw.board : null);

  // The door returns newest-first. Track which lineages have already checked
  // OUT while scanning newest→oldest; the first still-open CCWA_CHECKIN wins.
  const closedLineages = new Set();
  let current = null;
  for (const row of board) {
    const lineageKey = str(row?.lineage_id) || str(row?.coder) || '';
    if (isCheckout(row)) {
      closedLineages.add(lineageKey);
      continue;
    }
    if (isCheckin(row) && !closedLineages.has(lineageKey)) {
      current = row;
      break;
    }
  }

  if (!current) {
    return {
      identity: normalizeIdentity(null),
      lineage: [],
      status: CODER_STATUS.IDLE,
      currentWork: null,
      report: filterReport(null),
      cycle: normalizeCycleStamp(null),
      present: false,
    };
  }

  const identity = normalizeIdentity(current);
  const lineage = normalizeLineage(current.lineage_id);
  const status = normalizeStatus(current.status) === CODER_STATUS.UNKNOWN
    ? CODER_STATUS.CHECKED_IN
    : normalizeStatus(current.status);
  const currentWork = str(current.act) || str(current.intent) || null;
  // touching (files/surfaces) + receipts are the coder's report of what it is
  // handling; filtered reporting suppresses founder/private keys and escalates
  // serious ones.
  const report = filterReport(toArray(current.touching).concat(toArray(current.receipts)));
  const cycle = normalizeCycleStamp(current.lineage || current.acl_stamp || current.at);

  return { identity, lineage, status, currentWork, report, cycle, present: true };
}

/**
 * The canonical brain source-key PREFIX for temporary-coder check-in rows, and
 * the ledger read path. Kept here (not hardcoded at every call site) so the
 * hook and any ops surface agree on the one contract. These name locations and
 * an endpoint, never founder data. Matches anew routes/ccwa.checkin.routes.js
 * and core/ccwa.first.js exactly.
 */
export const CODER_CHECKIN_SOURCE_PREFIX = 'ccwa.cc.checkin.';
export const CODER_CHECKIN_LEDGER_PATH = '/ccwa/ledger';
